import React, { createContext, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// ── Context shape ─────────────────────────────────────────────────────────────

interface CoachViewContextValue {
  /** The athlete's userId the coach is currently viewing, or null if not in coach mode */
  viewAs: string | null;
  /** The athlete's display name (from URL param), or null */
  viewAsName: string | null;
  /** true when a coach is actively viewing an athlete's data */
  isCoachView: boolean;
  /** The CoachRelationship ID for this coach↔athlete pair — needed to post/fetch contextual comments */
  relationshipId: string | null;
}

const CoachViewContext = createContext<CoachViewContextValue>({
  viewAs: null,
  viewAsName: null,
  isCoachView: false,
  relationshipId: null,
});

export const useCoachView = () => useContext(CoachViewContext);

// ── Provider ──────────────────────────────────────────────────────────────────
// Must be rendered inside a React Router <Router> so useSearchParams works.

export const CoachViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();

  const viewAs         = searchParams.get('viewAs')         || null;
  const viewAsName     = searchParams.get('viewAsName')     || null;
  const relationshipId = searchParams.get('relationshipId') || null;
  const isCoachView = !!viewAs;

  const value = useMemo(
    () => ({ viewAs, viewAsName, isCoachView, relationshipId }),
    [viewAs, viewAsName, isCoachView, relationshipId],
  );

  return (
    <CoachViewContext.Provider value={value}>
      {children}
    </CoachViewContext.Provider>
  );
};
